import React from 'react';
import { motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { formatDay } from '@/data/catalog';
import { Clock, MapPin, Globe, ArrowUpRight, AlertTriangle } from 'lucide-react';

export const ShowCard = ({ show, index = 0, onOpen, showDates = true }) => {
  const { t, lang } = useLang();
  const isInstall = show.tipus === 'installacio';
  const isCancelled = !!show.cancelat;

  return (
    <motion.button
      data-testid={`show-card-${show.id}`}
      onClick={() => onOpen(show)}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.04, ease: 'easeOut' }}
      className={`group text-left flex flex-col overflow-hidden rounded-2xl bg-[#0b241b] transition-colors duration-200 ${
        isCancelled
          ? 'border border-[var(--cea-lamp-red)]/45 hover:border-[var(--cea-lamp-red)]/70'
          : 'border border-[var(--cea-border)] hover:bg-[#12382c] hover:border-[var(--cea-border-strong)] hover:shadow-[0_0_22px_rgba(51,255,204,0.16)]'
      }`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={show.imagen}
          alt={show.titol}
          loading="lazy"
          className={`h-full w-full object-cover transition-transform duration-500 ${
            isCancelled ? 'grayscale opacity-50' : 'group-hover:scale-105'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040f0c] via-transparent to-transparent opacity-80" />
        <div className="absolute top-3 left-3 flex gap-2">
          {isCancelled ? (
            <span className="rounded-full bg-[var(--cea-lamp-red)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
              {t.avis.badge}
            </span>
          ) : (
            <span className="rounded-full bg-[var(--cea-accent)] px-2.5 py-1 text-[11px] font-bold text-[#040f0c]">
              {show.typeBadge ? show.typeBadge[lang] : (isInstall ? t.agenda.installation : t.agenda.free)}
            </span>
          )}
        </div>
        {showDates && (show.dia || show.rangLabel) && (
          <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#040f0c]/80 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-white">
            {show.rangLabel ? show.rangLabel[lang] : formatDay(show.dia, t.months)}{show.hora ? ` · ${show.hora}` : ''}
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className={`font-display font-medium text-lg leading-tight tracking-[-0.01em] ${
            isCancelled ? 'text-white/60 line-through decoration-[var(--cea-lamp-red)] decoration-2' : ''
          }`}>{show.titol}</h3>
          <ArrowUpRight size={18} className="flex-shrink-0 text-white/40 group-hover:text-[var(--cea-accent)] transition-colors duration-200" />
        </div>
        <p className={`mt-1 text-sm font-semibold ${isCancelled ? 'text-white/45' : 'text-[var(--cea-accent)]'}`}>{show.compania}</p>

        {isCancelled && (
          <p data-testid={`show-card-cancelled-${show.id}`} className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[var(--cea-lamp-red)]">
            <AlertTriangle size={13} /> {t.avis.cancelled}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-white/60">
          <span className="inline-flex items-center gap-1"><Globe size={12} /> {show.pais[lang]}</span>
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {show.ubicacio}</span>
          {showDates && show.hora && <span className="inline-flex items-center gap-1"><Clock size={12} /> {show.hora}</span>}
        </div>

        <p className="mt-3 text-sm text-white/70 leading-relaxed line-clamp-3">{show.sinopsis[lang]}</p>

        <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
          <span className="rounded-full border border-[var(--cea-border)] px-2.5 py-1 text-[11px] font-semibold text-white/80">
            {show.disciplina[lang]}
          </span>
          <span className="text-xs font-bold text-[var(--cea-accent)] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {t.agenda.details} →
          </span>
        </div>
      </div>
    </motion.button>
  );
};
